/**
 * Bear Export Transformation Module
 *
 * This module provides pure functions for transforming Bear export markdown
 * content into NotePlan-compatible format. All transformations are applied
 * during the import batch building phase, before Craft transforms and link parsing.
 *
 * @module bearTransforms
 */

/**
 * Detects if text contains Bear-specific syntax
 *
 * Checks for Bear's embedded attachment syntax:
 * [alt](path)<!-- {"embed":"true"} -->
 *
 * @param text - Text content to check
 * @returns true if Bear syntax detected, false otherwise
 */
export function detectBearSyntaxInText(text: string): boolean {
	// Match Bear's embed comment syntax
	const BEAR_EMBED_PATTERN = /\[([^\]]*)\]\(([^)]+)\)<!--\s*\{[^}]*"embed"\s*:\s*"true"[^}]*\}\s*-->/
	return BEAR_EMBED_PATTERN.test(text)
}

/**
 * Applies all Bear-specific transformations to markdown content
 *
 * Currently handles:
 * 1. Transform embedded attachments: [alt](path)<!-- {"embed":"true"} --> → ![alt](path)
 *
 * This function is idempotent - it's safe to call on non-Bear content
 * as it will only transform Bear-specific syntax.
 *
 * @param content - Original markdown content
 * @returns Transformed markdown content
 */
export function applyBearTransforms(content: string): string {
	return transformBearEmbeds(content)
}

/**
 * Transforms Bear embedded attachments to standard markdown image syntax
 *
 * Converts: [alt](path)<!-- {"embed":"true"} --> → ![alt](path)
 * Also handles: [alt](path)<!-- {"embed":"true", "preview":"true"} --> → ![alt](path)
 *
 * The transformation:
 * - Adds ! prefix to make it a markdown image
 * - Removes the HTML comment entirely
 * - Preserves the alt text and path
 *
 * Note: Does not transform links that already have ! prefix
 *
 * @param content - Markdown content
 * @returns Content with Bear embeds transformed to image syntax
 */
function transformBearEmbeds(content: string): string {
	// Pattern: [alt](path)<!-- {"embed":"true", ...} --> → ![alt](path)
	// Negative lookbehind ensures we don't match already-correct image links
	// The embed comment can contain additional properties like "preview":"true"
	// Use lazy matching .*? to capture path until we hit )<!-- pattern
	const BEAR_EMBED_PATTERN =
		/(?<!!)\[([^\]]*)\]\((.*?)\)<!--\s*\{[^}]*"embed"\s*:\s*"true"[^}]*\}\s*-->/g

	return content.replaceAll(
		BEAR_EMBED_PATTERN,
		(_match, alt: string, path: string) => {
			return `![${alt}](${path})`
		}
	)
}

